import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { memberApi, publicApi, getMemberToken, setMemberToken, clearMemberToken } from "../lib/api";

const MemberAuthContext = createContext(null);

export const MemberAuthProvider = ({ children }) => {
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unread, setUnread] = useState(0);

  const refresh = useCallback(async () => {
    if (!getMemberToken()) { setMember(null); setLoading(false); return; }
    try {
      const { data } = await memberApi.get("/member/me");
      setMember(data.member);
    } catch { clearMemberToken(); setMember(null); }
    finally { setLoading(false); }
  }, []);

  const refreshUnread = useCallback(async () => {
    if (!getMemberToken()) return;
    try { const { data } = await memberApi.get("/member/messages/unread-count"); setUnread(data.unread || 0); }
    catch {}
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    if (!member) return;
    refreshUnread();
    const id = setInterval(refreshUnread, 30000);
    return () => clearInterval(id);
  }, [member, refreshUnread]);

  const login = async (email, password) => {
    const { data } = await publicApi.post("/member/login", { email, password });
    setMemberToken(data.token); setMember(data.member); return data.member;
  };
  const activate = async (token, password) => {
    const { data } = await publicApi.post(`/activate/${token}`, { password });
    setMemberToken(data.token); setMember(data.member); return data.member;
  };
  const logout = () => { clearMemberToken(); setMember(null); setUnread(0); };
  const updateProfile = async (updates) => {
    const { data } = await memberApi.patch("/member/me", updates);
    setMember(data.member); return data.member;
  };
  const updateAccount = async (updates) => {
    const { data } = await memberApi.patch("/member/account", updates);
    setMember(data.member); return data.member;
  };

  return (
    <MemberAuthContext.Provider value={{ member, loading, unread, login, activate, logout, refresh, refreshUnread, updateProfile, updateAccount }}>
      {children}
    </MemberAuthContext.Provider>
  );
};

export const useMemberAuth = () => {
  const ctx = useContext(MemberAuthContext);
  if (!ctx) throw new Error("useMemberAuth must be used within MemberAuthProvider");
  return ctx;
};
